import { useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import type { ImportIssue } from '@/api/clientImports/clientImportsApi.types';

interface CellIssueTooltipProps {
  issues: ImportIssue[];
  children: ReactNode;
}

interface TooltipPosition {
  top: number;
  left: number;
}

const TOOLTIP_WIDTH_PIXELS = 256;
const VIEWPORT_EDGE_MARGIN_PIXELS = 8;

export function CellIssueTooltip({ issues, children }: CellIssueTooltipProps) {
  const triggerRef = useRef<HTMLSpanElement>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [isPinned, setIsPinned] = useState(false);
  const [position, setPosition] = useState<TooltipPosition | null>(null);

  if (issues.length === 0) {
    return <>{children}</>;
  }

  const shouldShow = isHovered === true || isPinned === true;

  function recomputePosition() {
    const triggerElement = triggerRef.current;
    if (triggerElement === null) {
      return;
    }
    const rect = triggerElement.getBoundingClientRect();
    const maximumLeft = window.innerWidth - TOOLTIP_WIDTH_PIXELS - VIEWPORT_EDGE_MARGIN_PIXELS;
    const clampedLeft = Math.max(VIEWPORT_EDGE_MARGIN_PIXELS, Math.min(rect.left, maximumLeft));
    setPosition({ top: rect.bottom + 4, left: clampedLeft });
  }

  function handlePointerEnter() {
    setIsHovered(true);
    recomputePosition();
  }

  function handlePointerLeave() {
    setIsHovered(false);
  }

  function handleToggle() {
    setIsPinned(function toggle(previous) {
      return previous === false;
    });
    recomputePosition();
  }

  function handleKeyDown(keyboardEvent: KeyboardEvent<HTMLSpanElement>) {
    if (keyboardEvent.key === 'Enter' || keyboardEvent.key === ' ') {
      keyboardEvent.preventDefault();
      handleToggle();
    }
    if (keyboardEvent.key === 'Escape') {
      setIsPinned(false);
      setIsHovered(false);
    }
  }

  return (
    <span
      ref={triggerRef}
      className="relative block cursor-help"
      role="button"
      tabIndex={0}
      onMouseEnter={handlePointerEnter}
      onMouseLeave={handlePointerLeave}
      onFocus={handlePointerEnter}
      onBlur={handlePointerLeave}
      onClick={handleToggle}
      onKeyDown={handleKeyDown}
    >
      {children}
      {shouldShow === true && position !== null
        ? createPortal(
            <span
              style={{
                position: 'fixed',
                top: position.top,
                left: position.left,
                width: TOOLTIP_WIDTH_PIXELS,
              }}
              className="z-50 flex flex-col gap-1 rounded-lg bg-zooindigo p-2.5 text-xs text-zoowhite shadow-lg"
            >
              {issues.map(function renderMessage(issue, index) {
                return <span key={index}>{issue.message}</span>;
              })}
            </span>,
            document.body
          )
        : null}
    </span>
  );
}
